import { useEffect, useRef, type ReactNode } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useCreateDocument } from "../api/documents";
import { PageHeader } from "../layout/PageHeader";

export { DocumentScreen } from "../documents/DocumentScreen";
export { DocumentHistoryScreen } from "../revisions/DocumentHistoryScreen";

/**
 * `/doc/new` creates the document and moves on to it. The creation context
 * travels in the query string (note 03 §3.2): `collectionId`,
 * `parentDocumentId`, `templateId`, `index`, and `title` to pre-fill.
 *
 * A document created with somewhere to live is published there, so it appears
 * in the collection immediately; one created with nowhere to live is a draft.
 */
export function NewDocumentScreen(): ReactNode {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const create = useCreateDocument();
  const startedRef = useRef(false);

  const collectionId = searchParams.get("collectionId");
  const parentDocumentId = searchParams.get("parentDocumentId");
  const templateId = searchParams.get("templateId");
  const title = searchParams.get("title") ?? "";
  const index = searchParams.get("index");

  const mutate = create.mutate;
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    const parsedIndex = index === null ? Number.NaN : Number.parseInt(index, 10);
    mutate(
      {
        title,
        ...(collectionId === null ? {} : { collectionId }),
        ...(parentDocumentId === null ? {} : { parentDocumentId }),
        ...(templateId === null ? {} : { templateId }),
        ...(Number.isNaN(parsedIndex) ? {} : { index: parsedIndex }),
        ...(collectionId === null && parentDocumentId === null ? {} : { publish: true }),
      },
      {
        onSuccess: (document) => {
          void navigate(document.url, { replace: true });
        },
      },
    );
  }, [collectionId, index, mutate, navigate, parentDocumentId, templateId, title]);

  return (
    <>
      <PageHeader>
        <h1 className="truncate text-base font-semibold">New document</h1>
      </PageHeader>
      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">New document</h2>
        {create.isError ? (
          <p role="alert" className="mt-2 text-sm text-danger">
            {create.error.message}
          </p>
        ) : (
          <p role="status" className="mt-2 text-sm text-muted">
            Creating the document…
          </p>
        )}
      </div>
    </>
  );
}

/** `/d/:slug` is the short form of a document URL (note 03 §3.2). */
export function DocumentShortRedirect(): ReactNode {
  const { slug } = useParams();
  return <Navigate to={`/doc/${slug ?? ""}`} replace />;
}
